import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import SwapRightOutlined from '@ant-design/icons/SwapRightOutlined';
import { Button, DatePicker, Dropdown } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const { RangePicker } = DatePicker;

type MoreOptions = keyof typeof extendDateOptions;

export const extendDateOptions = {
  Yesterday: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
  'Last 7 days': [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
  'Last 30 days': [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
  'This Week': [dayjs().startOf('week'), dayjs().endOf('day')],
  'This Month': [dayjs().startOf('month'), dayjs().endOf('day')],
  'This Quarter': [dayjs().subtract(3, 'month').startOf('day'), dayjs().endOf('day')],
  'This Year': [dayjs().startOf('year'), dayjs().endOf('year').endOf('day')],
  'Last Month': [
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month').endOf('day'),
  ],
  'Last Year': [
    dayjs().subtract(1, 'year').startOf('year'),
    dayjs().subtract(1, 'year').endOf('year').endOf('day'),
  ],
  Today: [dayjs().startOf('day'), dayjs().endOf('day')],
  'All Time': [dayjs('19750430').startOf('day'), dayjs().endOf('day')],
};

export interface DateRangePickerProps {
  readonly defaultValue?: [dayjs.Dayjs, dayjs.Dayjs]; // Default value of date range [start, end]
  readonly value?: [dayjs.Dayjs, dayjs.Dayjs]; // [start, end]
  readonly format?: string; // To set date format, default DD/MM/YYYY
  readonly baseTime?: dayjs.Dayjs; // To set the base time of system, for "All Time" select, default to 19750430
  readonly suffix?: React.ReactNode; // Suffix icon
  readonly size?: 'large' | 'middle' | 'small'; // Set size of selection box
  readonly width?: number; // Set width of selection box
  readonly disabledDate?: RangePickerProps['disabledDate']; // Disable date
  readonly allowClear?: boolean; // Allow clear date
  readonly onChange: (dates: dayjs.Dayjs[] | string[], dateStrings?: [string, string]) => void;
  readonly moreOptions?: MoreOptions[]; // Add more options to the preset ranges
}

const defaultDisabledDate: RangePickerProps['disabledDate'] = (current) => {
  // Can not select days after today and today
  return current && current > dayjs().endOf('day');
};

const DateRangePicker = ({
  value,
  format = 'DD/MM/YYYY',
  onChange,
  suffix,
  size = 'small',
  width = 200,
  baseTime = dayjs('19750430', 'YYYYMMDD'),
  disabledDate,
  allowClear = false,
  moreOptions = [
    'Yesterday',
    'Last 7 days',
    'Last 30 days',
    'This Month',
    'This Year',
    'Last Year',
  ],
  ...rest
}: DateRangePickerProps): JSX.Element => {
  const { t } = useTranslation('Vindoc');
  const today = dayjs();
  const presetRanges = {
    Today: [today, today],
  };

  if (moreOptions?.length) {
    Array.from(new Set(moreOptions)).forEach((option) => {
      presetRanges[option] = extendDateOptions[option];
    });
  }
  presetRanges['All Time'] = [baseTime, today];

  const getDisplayValue = (value = []) => {
    const [start, end] = value;

    if (!start || !end) return '';
    const keys = Object.keys(presetRanges);

    for (const key of keys) {
      const [checkStart, checkEnd] = presetRanges[key];

      if (
        start.format(format) == checkStart.format(format) &&
        end.format(format) == checkEnd.format(format)
      ) {
        return <>{t(key)}</>;
      }
    }
    if (end >= presetRanges['All Time'][1] && start <= presetRanges['All Time'][0]) {
      return <>{t('All Time')}</>;
    }

    return (
      <>
        {start.format(format)} <SwapRightOutlined /> {end.format(format)}
      </>
    );
  };

  const [isCustom, setIsCustom] = useState(false);
  const displayValue: React.ReactNode = getDisplayValue(value);

  const menuItems = Object.entries(presetRanges).map(([key, value]) => ({
    key,
    label: t(key),
    onClick: () => onChange(value, [value[0].format(format), value[1].format(format)]),
  }));

  const menu = {
    items: [
      ...menuItems,
      {
        key: 'custom',
        label: t('Custom'),
        onClick: () => setIsCustom(true),
      },
    ],
  };

  return (
    <>
      {!isCustom && (
        <Dropdown
          menu={menu}
          placement="bottomLeft"
          trigger={['click']}
          overlayClassName="vindr-dropdown dropdown-options-dark"
        >
          <Button
            size={size}
            style={{
              width,
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {displayValue || <span className="placeholder">{t('Select date range')}</span>}
            {suffix || <CaretDownOutlined style={{ color: 'rgba(255, 255, 255, 0.3)' }} />}
          </Button>
        </Dropdown>
      )}
      {isCustom && (
        <RangePicker
          picker="date"
          open={isCustom}
          className="studylist-datepicker"
          style={{ width }}
          onChange={(values) => {
            onChange(values, [values[0].format(format), values[1].format(format)]);
            setIsCustom(false);
          }}
          onOpenChange={(flag) => setIsCustom(flag)}
          disabledDate={disabledDate || defaultDisabledDate}
          {...{ size, format, allowClear }}
          {...rest}
        />
      )}
    </>
  );
};

export default DateRangePicker;
